<template>
  <Card :loading="loading" data-id="series-card" no-body no-card>
    <template #header>
      <CardHeader :title="title" :icon="icon" :subtitle="subtitle">
        <template #default>
          <slot name="actions" />
        </template>
      </CardHeader>
    </template>
    <div
      class="relative h-[300px] w-full"
      data-el="series-card-chart"
      :data-test-id="testId"
      :data-loading="loading ? '1' : '0'"
      :data-title="title"
      :data-chart-key="chartKey.toString(10)"
    >
      <Chart
        v-if="!loading && series"
        :key="chartKey"
        :options="chartOptions"
        :data="chartData"
      />
    </div>
  </Card>
</template>
<script lang="ts" setup>
import Card from '~/components/tw/Card.vue';
import { computed, ref, watch } from 'vue';
import type { UsageRecordSeries } from '@mailslurp/admin-sdk';
import { Line as Chart } from 'vue-chartjs';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import CardHeader from '~/components/CardHeader.vue';
import type { MsIcon } from '~/lib/services/types';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
  BarElement,
  CategoryScale,
  LinearScale,
);
const chartKey = ref(0);

const props = withDefaults(
  defineProps<{
    series?: UsageRecordSeries;
    loading: boolean;
    title: string;
    subtitle?: string;
    icon?: MsIcon;
    testId?: string;
  }>(),
  {
    testId: 'series-card',
    series: undefined,
    subtitle: undefined,
    icon: 'usageSeries',
    loading: false,
  },
);

const chartData = computed(() => {
  return {
    labels: props.series?.dates ?? [],
    // https://www.chartjs.org/docs/latest/charts/line.html
    datasets: [
      {
        label: props.title,
        fill: false,
        borderColor: 'rgb(75, 192, 192)',
        tension: 0.1,
        data: props.series?.amounts ?? [],
      },
    ],
  };
});
const chartOptions = computed(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 0,
        beginAtZero: true,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };
});

watch(
  () => props.series,
  () => {
    // Increment the chartKey to force re-render of the chart
    chartKey.value++;
  },
  { deep: true },
);
</script>
